import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { TFunction } from 'i18next'
import React, { FC, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Authorization from '../../../gateway/Authentication/Authorization'
import DialogContext from '../../../gateway/Authentication/DialogContext'
import { EditDialogProps } from '../../../gateway/components/EditDialog'
import ProtectedButton from '../../../gateway/components/ProtectedButton'
import Loading from '../../../gateway/layout/Loading'
import ConnectedAppService from '../service/ConnectedAppService'
import TechnicalUserService from '../service/TechnicalUserService'

const ConnectedAppAndUser: FC<{ appId: number }> = ({ appId }) => {
  const { t } = useTranslation()
  const { editDialogPropsSetter } = useContext(DialogContext)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadData() {}
    loadData().finally(() => setLoading(false))
  }, [])

  return (
    <Loading isLoading={loading}>
      <>
        <ProtectedButton
          permissions={Authorization.salesforce.edit}
          tooltip={t('salesforce.connectedApp.create.toolTip')}
          className='text-decoration-none text-left ml-3 mr-4'
          onClick={() => createConnectedAppDialog(t, editDialogPropsSetter, appId)}>
          <FontAwesomeIcon icon={faPlus} />
          &nbsp;&nbsp;&nbsp;
          {t('salesforce.connectedApp.create.label')}
        </ProtectedButton>
        <ProtectedButton
          permissions={Authorization.salesforce.edit}
          tooltip={t('salesforce.techUser.create.toolTip')}
          className='text-decoration-none text-left ml-3 mr-4'
          onClick={() => createTechnicalUserDialog(t, editDialogPropsSetter, appId)}>
          <FontAwesomeIcon icon={faPlus} />
          &nbsp;&nbsp;&nbsp;
          {t('salesforce.techUser.create.label')}
        </ProtectedButton>
      </>
    </Loading>
  )
}

function createConnectedAppDialog(t: TFunction, editDialogPropsSetter: any, appId: number) {
  const editDialogProps: EditDialogProps = {
    show: true,
    labelGridSize: 3,
    header: t('salesforce.connectedApp.create.label'),
    onSubmit: ConnectedAppService.createConnectedApp,
    onSubmitExtraArgs: [appId, t],
    data: [
      // App Data
      {
        id: 'appId',
        type: 'hidden',
        required: true,
        value: String(appId),
      },
      // Admin login data
      {
        id: 'adminLogin',
        type: 'text',
        label: t('salesforce.global.adminLogin.label'),
        placeholder: t('salesforce.global.adminLogin.label'),
        requiredMessage: t('salesforce.global.adminLogin.invalid'),
        required: true,
      },
      {
        id: 'adminPassword',
        type: 'password',
        label: t('salesforce.global.adminPassword.label'),
        placeholder: t('salesforce.global.adminPassword.label'),
        requiredMessage: t('salesforce.global.adminPassword.invalid'),
        required: true,
      },
      {
        id: 'adminToken',
        type: 'password',
        label: t('salesforce.global.adminToken.label'),
        placeholder: t('salesforce.global.optional.placeHolder'),
        required: false,
      },
      {
        id: 'loginHost',
        type: 'text',
        label: t('salesforce.global.loginHost.label'),
        placeholder: 'login.salesforce.com',
        requiredMessage: t('salesforce.global.loginHost.invalid'),
        required: true,
        value: 'login.salesforce.com',
      },
      // Connected App data
      {
        id: 'label',
        type: 'text',
        label: t('salesforce.connectedApp.label.label'),
        placeholder: t('salesforce.connectedApp.label.label'),
        requiredMessage: t('salesforce.connectedApp.label.invalid'),
        required: true,
      },
      {
        id: 'contactEmail',
        type: 'text',
        label: t('salesforce.connectedApp.contactEmail.label'),
        placeholder: t('salesforce.connectedApp.contactEmail.label'),
        requiredMessage: t('salesforce.connectedApp.contactEmail.invalid'),
        required: true,
      },
      {
        id: 'callbackHost',
        type: 'text',
        label: t('salesforce.connectedApp.callbackHost.label'),
        placeholder: 'auth.mygateway.com',
        requiredMessage: t('salesforce.connectedApp.callbackHost.invalid'),
        required: true,
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

function createTechnicalUserDialog(t: TFunction, editDialogPropsSetter: any, appId: number) {
  const editDialogProps: EditDialogProps = {
    show: true,
    labelGridSize: 3,
    header: t('salesforce.techUser.create.label'),
    onSubmit: TechnicalUserService.createTechnicalUser,
    onSubmitExtraArgs: [appId, t],
    data: [
      // App Data
      {
        id: 'appId',
        type: 'hidden',
        required: true,
        value: String(appId),
      },
      // Admin login data
      {
        id: 'adminLogin',
        type: 'text',
        label: t('salesforce.global.adminLogin.label'),
        placeholder: t('salesforce.global.adminLogin.label'),
        requiredMessage: t('salesforce.global.adminLogin.invalid'),
        required: true,
      },
      {
        id: 'adminPassword',
        type: 'password',
        label: t('salesforce.global.adminPassword.label'),
        placeholder: t('salesforce.global.adminPassword.label'),
        requiredMessage: t('salesforce.global.adminPassword.invalid'),
        required: true,
      },
      {
        id: 'adminToken',
        type: 'password',
        label: t('salesforce.global.adminToken.label'),
        placeholder: t('salesforce.global.optional.placeHolder'),
        required: false,
      },
      {
        id: 'loginHost',
        type: 'text',
        label: t('salesforce.global.loginHost.label'),
        placeholder: 'login.salesforce.com',
        requiredMessage: t('salesforce.global.loginHost.invalid'),
        required: true,
        value: 'login.salesforce.com',
      },
      // Technical data
      {
        id: 'firstName',
        type: 'text',
        label: t('salesforce.techUser.firstName.label'),
        placeholder: t('salesforce.global.optional.placeHolder'),
        required: false,
      },
      {
        id: 'lastName',
        type: 'text',
        label: t('salesforce.techUser.lastName.label'),
        placeholder: t('salesforce.techUser.lastName.label'),
        requiredMessage: t('salesforce.techUser.lastName.invalid'),
        required: true,
      },

      {
        id: 'username',
        type: 'text',
        label: t('salesforce.techUser.username.label'),
        placeholder: t('salesforce.global.optional.placeHolder'),
        required: false,
      },
      {
        id: 'eMail',
        type: 'text',
        label: t('salesforce.techUser.eMail.label'),
        placeholder: t('salesforce.techUser.eMail.label'),
        requiredMessage: t('salesforce.techUser.eMail.invalid'),
        required: true,
      },
      {
        id: 'alias',
        type: 'text',
        label: t('salesforce.techUser.alias.label'),
        placeholder: t('salesforce.global.optional.placeHolder'),
        required: false,
      },
      {
        id: 'password',
        type: 'password',
        label: t('salesforce.techUser.password.label'),
        placeholder: t('salesforce.global.optional.placeHolder'),
        minLength: '8',
        required: false,
      },
      {
        id: 'nickname',
        type: 'text',
        label: t('salesforce.techUser.nickname.label'),
        placeholder: t('salesforce.global.optional.placeHolder'),
        required: false,
      },
    ],
  }

  editDialogPropsSetter(editDialogProps)
}

export default ConnectedAppAndUser
